import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../Button/Button';
import { RefObject } from 'react';
import { ReactComponent as Icon } from './assets/OCAIcon.svg';
import { ReactComponent as Logo } from './assets/OCALogo.svg';
import styled from 'styled-components';
import { AnimatedHamburger } from './components/AnimatedBurguer';

type SectionId = 'WHYOCA' | 'FAQS' | 'BLOG' | 'CONTACT';
type SectionRefs = Record<SectionId, RefObject<HTMLElement>>;

interface MobileMenuProps {
  sectionRefs: SectionRefs;
  isLanding?: boolean;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ sectionRefs, isLanding }) => {
  const [open, setOpen] = useState(false);

  const topBarItems: { name: string; id: SectionId; controlID: string }[] = [
    { name: 'Why OnCampus', id: 'WHYOCA', controlID: 'nav-menu-/why-oncampus' },
    { name: 'FAQS', id: 'FAQS', controlID: 'nav-menu-/faq' },
    {
      name: 'Blog',
      id: 'BLOG',
      controlID: 'nav-menu-https://blog.oncampusabroad.com/'
    },
    { name: 'Contact', id: 'CONTACT', controlID: 'nav-menu-/contact' }
  ];
  const BLOG_URL = 'https://blog.oncampusabroad.com/';
  const textButton = 'Buy Insurance';
  const hrefButton = '/onboarding';
  const idButton = 'NavigationMenu_BUY-INSURANCE_Button';

  const toggleMenu = () => setOpen(!open);

  const handleScroll = (id: SectionId) => {
    if (id === 'BLOG') {
      window.open(BLOG_URL, '_blank', 'noopener,noreferrer');
      setOpen(false);
      return;
    }

    if (window.location.pathname !== '/') {
      window.location.href = `/?id=${id}`;
      return;
    }

    setOpen(false);
    sectionRefs[id]?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return createPortal(
    <>
      <WrapperNav>
        <NavBar open={open} isLanding={isLanding}>
          <LogoWrapper open={open}>
            <a href='/' id='nav-menu-/logo'>
              <Logo />
            </a>
          </LogoWrapper>
          {isLanding === true && (
            <AnimatedHamburger onClick={toggleMenu} isOpen={open} />
          )}
        </NavBar>
      </WrapperNav>
      <MenuOverlay open={open}>
        <IconWrapper open={open}>
          <a href='/' id='nav-menu-/logo'>
            <Icon />
          </a>
        </IconWrapper>
        <BottomWrapper>
          <MenuItems>
            {topBarItems.map(item => (
              <MenuItem
                key={item.id}
                onClick={() => handleScroll(item.id)}
                id={item.controlID}
              >
                {item.name}
              </MenuItem>
            ))}
          </MenuItems>
          <ButtonWrapper>
            <CustomButton href={hrefButton} id={idButton}>
              {textButton}
            </CustomButton>
          </ButtonWrapper>
        </BottomWrapper>
      </MenuOverlay>
    </>,
    document.body
  );
};

export default MobileMenu;

const WrapperNav = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  widt: 100%;
  width: 100%;
  position: fixed;
  top: 38px;
  padding: 0 20px;
  z-index: 9999999999;
`;

const NavBar = styled.div<{ open: boolean; isLanding?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${props => (props.isLanding ? '0 0 0 20px' : '10px 20px')};
  min-height: 50px;
  z-index: 9999999999;

  background: ${props =>
    props.open ? 'transparent' : 'rgba(255, 246, 239, 0.33)'};
  backdrop-filter: ${props => (props.open ? 'none' : 'blur(12px)')};
  -webkit-backdrop-filter: ${props => (props.open ? 'none' : 'blur(12px)')};
  box-shadow: ${props =>
    props.open ? 'none' : '0 4px 30px rgba(0, 0, 0, 0.1)'};

  border-radius: 100px;
`;

const MenuOverlay = styled.div<{ open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 100vw;
  height: 100vh;
  background: #05c793;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  clip-path: ${props =>
    props.open ? 'circle(150% at top right)' : 'circle(0% at top right)'};
  transition: clip-path 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  z-index: 99999;
  overflow: hidden;
`;

const BottomWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const IconWrapper = styled.div<{ open: boolean }>`
  position: absolute;
  top: 28px;
  left: 23px;
  margin-bottom: 40px;

  opacity: ${props => (props.open ? 1 : 0)};
  transform: ${props => (props.open ? 'scale(1)' : 'scale(0.8)')};
  transition: opacity 0.4s ease, transform 0.4s ease;
`;

const LogoWrapper = styled.div<{ open: boolean }>`
  opacity: ${props => (props.open ? 0 : 1)};
  transform: ${props => (props.open ? 'scale(0.8)' : 'scale(1)')};
  transition: opacity 0.4s ease, transform 0.4s ease;
`;

const MenuItems = styled.nav`
  display: flex;
  flex-direction: column;
  text-align: center;
  width: 100%;
`;

const MenuItem = styled.button`
  all: unset;
  color: #283288;
  text-align: center;
  font-family: 'League Spartan';
  font-size: 20px;
  font-weight: 500;
  line-height: normal;
  cursor: pointer;
  transition: transform 0.3s ease, opacity 0.3s ease;
  border-bottom: 1px solid #283288;
  padding: 32px 16px;

  &:first-child {
    border-top: 1px solid #283288;
  }

  &:last-child {
    border-bottom: 1px solid #283288;
  }

  &:hover {
    color: darkenColor(backgroundColor, 0.08);
    transform: scale(1.1);
  }
`;

const ButtonWrapper = styled.div`
  margin-top: 32px;
`;

const CustomButton = styled(Button)`
  width: 162px;
`;
